import type { ErrorComponentProps } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'

import { StatusPage } from '@/components/StatusPage'
import { Button } from '@/components/ui/button'

/** One-line title per HTTP status; anything unmapped falls back to 500's. */
const STATUS_TITLES: Record<number, string> = {
  400: 'That request didn’t make sense.',
  401: 'You need to sign in for this.',
  403: 'You don’t have access to this.',
  408: 'That took too long. Try again.',
  429: 'Too many requests. Give it a minute.',
  500: 'Something went wrong on our end.',
  502: 'Something went wrong upstream.',
  503: 'Temporarily unavailable. Try again shortly.',
}

/**
 * `defaultErrorComponent` on the router (see `src/router.tsx`) — the fallback
 * UI for any render/loader error caught by a TanStack Router error boundary.
 * Dynamic per error type: if the thrown error carries an HTTP-ish `status`
 * (e.g. a `PublicError` from a server fn, which survives serialization as a
 * plain object with `status`), the page shows that code and a matching line;
 * everything else renders as a 500. We never surface `error.message` or a
 * stack in production — `error` may be a raw render-time throw that never
 * passed through `sanitizeErrors` (see AGENTS.md's no-internals rule).
 */
export default function ErrorPage({ error, reset }: ErrorComponentProps) {
  const status = errorStatus(error)
  const devDetail = import.meta.env.DEV ? errorDetail(error) : null

  return (
    <StatusPage
      code={status}
      title={STATUS_TITLES[status] ?? STATUS_TITLES[500]}
    >
      <Button size="lg" onClick={() => reset()}>
        Try again
      </Button>
      <Button asChild size="lg" variant="outline">
        <Link to="/">Go home</Link>
      </Button>
      {devDetail ? (
        <pre className="mt-4 w-full max-w-xl overflow-x-auto rounded-lg border bg-muted/50 px-4 py-3 text-left font-mono text-xs text-muted-foreground">
          {devDetail}
        </pre>
      ) : null}
    </StatusPage>
  )
}

/**
 * Best-effort HTTP status from an unknown thrown value. Instance checks are
 * unreliable here (server-fn errors are re-created from serialized data on
 * the client), so read a numeric `status`/`statusCode` property if present.
 */
function errorStatus(error: unknown): number {
  if (typeof error === 'object' && error !== null) {
    for (const key of ['status', 'statusCode'] as const) {
      const value = (error as Record<string, unknown>)[key]
      if (typeof value === 'number' && value >= 400 && value <= 599) {
        return value
      }
    }
  }
  return 500
}

/** Dev-only formatting of the error for the debug block. Never used in prod. */
function errorDetail(error: unknown): string {
  if (error instanceof Error) return error.stack || error.message
  return String(error)
}
